import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface UserCoupon {
  id: string;
  user_id: string;
  coupon_id: string;
  provider_id: string;
  is_active: boolean;
  times_used: number;
  applied_at: string;
  created_at: string;
  updated_at: string;
  coupon: {
    id: string;
    code: string;
    name: string;
    description?: string;
    discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
    discount_value: number;
    credits_per_period?: number;
    is_active: boolean;
    valid_from: string;
    valid_until?: string;
    provider_id: string;
  };
}

export const useUserCoupons = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-coupons', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_coupons')
        .select(`
          *,
          coupon:coupons(
            id,
            code,
            name,
            description,
            discount_type,
            discount_value,
            credits_per_period,
            is_active,
            valid_from,
            valid_until,
            provider_id
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching user coupons:', error);
        throw error;
      }

      return data as UserCoupon[];
    },
    enabled: !!user,
  });
};

export const useLinkCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (couponCode: string) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const upperCode = couponCode.trim().toUpperCase();

      // Find the coupon by code, case-insensitive
      const { data: coupon, error: findError } = await supabase
        .from("coupons")
        .select("id,provider_id,is_active,usage_limit,used_count,valid_from,valid_until,code,name,discount_type,credits_per_period")
        .ilike("code", upperCode)
        .maybeSingle();

      if (findError) {
        throw new Error("There was an error searching for the coupon. Please try again.");
      }

      if (!coupon) {
        throw new Error("This coupon does not exist or is invalid.");
      }

      // Check if coupon is active and within valid date range (if set)
      const now = new Date();
      const validFrom = coupon.valid_from ? new Date(coupon.valid_from) : null;
      const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;

      if (
        !coupon.is_active ||
        (validFrom && now < validFrom) ||
        (validUntil && now > validUntil) ||
        (coupon.usage_limit && coupon.used_count >= coupon.usage_limit)
      ) {
        throw new Error("This coupon is no longer valid or has reached its limit.");
      }

      // Check if already linked
      const { data: userCoupon, error: userCouponError } = await supabase
        .from("user_coupons")
        .select("id, is_active")
        .eq("user_id", user.id)
        .eq("coupon_id", coupon.id)
        .maybeSingle();

      if (userCoupon && userCoupon.is_active) {
        throw new Error("You have already linked this coupon.");
      }

      // If previously removed (inactive), reactivate it
      if (userCoupon && !userCoupon.is_active) {
        const { error: reactivateError } = await supabase
          .from("user_coupons")
          .update({ 
            is_active: true, 
            updated_at: new Date().toISOString() 
          })
          .eq("id", userCoupon.id);

        if (reactivateError) {
          throw new Error("Failed to reactivate coupon. Please try again.");
        }

        // Increment usage count
        const { error: updateError } = await supabase
          .from("coupons")
          .update({ 
            used_count: coupon.used_count + 1,
            updated_at: new Date().toISOString()
          })
          .eq("id", coupon.id);

        if (updateError) {
          console.error("Error updating coupon usage count:", updateError);
        }

        // Initialize credits if credit-based
        const isCreditBased = coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits';
        if (isCreditBased && coupon.credits_per_period) {
          const periodType = coupon.discount_type === 'weekly_credits' ? 'weekly' : 'monthly';
          try {
            await supabase.rpc('get_or_create_period_credits', {
              p_user_coupon_id: userCoupon.id,
              p_credits_per_period: coupon.credits_per_period,
              p_period_type: periodType
            });
          } catch (creditError) {
            console.error("Error initializing credits:", creditError);
          }
        }

        return coupon;
      }

      // Link the coupon
      const { data: linkData, error: linkError } = await supabase.from("user_coupons").insert([
        {
          user_id: user.id,
          coupon_id: coupon.id,
          provider_id: coupon.provider_id,
          is_active: true,
          times_used: 0,
        },
      ]).select().single();

      if (linkError) {
        throw new Error(linkError.message);
      }

      // Increment the coupon usage count
      const { error: updateError } = await supabase
        .from("coupons")
        .update({ 
          used_count: coupon.used_count + 1,
          updated_at: new Date().toISOString()
        })
        .eq("id", coupon.id);

      if (updateError) {
        console.error("Error updating coupon usage count:", updateError);
        // Note: We don't throw here since the coupon was already linked successfully
      }

      // If it's a credit-based coupon, initialize the credits for current period
      const isCreditBased = coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits';
      if (isCreditBased && coupon.credits_per_period) {
        const periodType = coupon.discount_type === 'weekly_credits' ? 'weekly' : 'monthly';
        
        try {
          await supabase.rpc('get_or_create_period_credits', {
            p_user_coupon_id: linkData.id,
            p_credits_per_period: coupon.credits_per_period,
            p_period_type: periodType
          });
        } catch (creditError) {
          console.error("Error initializing credits:", creditError);
          // Don't throw here as the coupon link was successful
        }
      }

      return coupon;
    },
    onSuccess: (coupon) => {
      // Toast notification removed - no longer showing success message
      
      // Invalidate all relevant queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ['user-coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupon-users', coupon.id] });
      queryClient.invalidateQueries({ queryKey: ['user-coupon-credits'] });
    },
    onError: (error: any) => {
      console.error("Coupon linking error:", error);
      toast({
        title: "Error linking coupon",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useRemoveCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (userCouponId: string) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Deactivate the user coupon instead of deleting it
      const { error } = await supabase
        .from('user_coupons')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', userCouponId)
        .eq('user_id', user.id); // Extra security check

      if (error) {
        console.error('Error removing coupon:', error);
        throw new Error('Failed to remove coupon. Please try again.');
      }

      return userCouponId;
    },
    onSuccess: () => {
      toast({
        title: "Coupon removed",
        description: "The coupon has been successfully removed from your account.",
      });
      
      // Invalidate queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['user-coupons'] });
      queryClient.invalidateQueries({ queryKey: ['user-coupon-credits'] });
    },
    onError: (error: any) => {
      console.error("Coupon removal error:", error);
      toast({
        title: "Error removing coupon",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
};
